import axios, { AxiosError } from "axios";
import {
  cancelReservation,
  confirmReservation,
  createMailboxReservation,
  getUploadedFileSize,
  ReservationCreationResult,
  updateReservation,
  uploadFileChunk,
} from "../api";
import {
  ApiException,
  CreateMailboxReservationRequest,
  ReservationObject,
  TransferDraft,
  TransferDraftFile,
  UpdateReservationRequest,
} from "../entities";
import { constants } from "../utils/constants";
import delay from "../utils/delay";

// ===================================================================================

type UploadData = {
  filename: string;
  clientId: string;
  totalBytes: number;
  file: File;
  startByte: number;
  endByte?: number;
};

type UploadProp = {
  reservationUid: string;
  reservationToken: string;
  transferCompletedBytes: number;
  transferTotalBytes: number;
  currentChunkSize: number;
  shouldCheckUploadedFileSize: boolean;
  uploadData: UploadData;
  retryCount: number;
  pushProgress: (progress: number, startByte: number) => void;
  signal?: AbortSignal;
};

// ===================================================================================

export class UploadAbortedError extends Error {
  constructor() {
    super("Upload aborted");
    this.name = "UploadAbortedError";
  }
}

type MimeTypeRejectedErrorFields = {
  fileName: string;
  clientId: string;
  reservationUid: string;
  reservationToken: string;
  completedBytes: number;
  totalBytes: number;
};

export class MimeTypeRejectedError extends Error {
  readonly fileName: string;
  readonly clientId: string;
  readonly reservationUid: string;
  readonly reservationToken: string;
  readonly completedBytes: number;
  readonly totalBytes: number;

  constructor(fields: MimeTypeRejectedErrorFields) {
    super(`File type not allowed: ${fields.fileName}`);
    this.name = "MimeTypeRejectedError";
    this.fileName = fields.fileName;
    this.clientId = fields.clientId;
    this.reservationUid = fields.reservationUid;
    this.reservationToken = fields.reservationToken;
    this.completedBytes = fields.completedBytes;
    this.totalBytes = fields.totalBytes;
  }
}

type UploadOptions = {
  signal?: AbortSignal;
};

export type ResumeContext = {
  reservationUid: string;
  reservationToken: string;
  files: TransferDraftFile[];
  completedBytes: number;
  totalBytes: number;
};

export type UploadResult = {
  reservationUid: string;
  reservationToken: string;
};

export default async function uploadMailboxTransfer(
  draft: TransferDraft,
  onProgress: (progress: number) => void,
  options: UploadOptions = {},
  vanityLink: string | undefined,
  resume?: ResumeContext
): Promise<UploadResult> {
  if (!vanityLink) {
    throw Error("vanity link cannot be undefined.");
  }

  const { signal } = options;

  throwIfAborted(signal);

  let reservationUid: string;
  let reservationToken: string;
  let filesToUpload: TransferDraftFile[];
  let totalFilesSize: number;
  let completedUploadSize: number;
  let currentUploadSize: number;

  if (resume) {
    reservationUid = resume.reservationUid;
    reservationToken = resume.reservationToken;
    filesToUpload = resume.files;
    totalFilesSize = resume.totalBytes;
    completedUploadSize = resume.completedBytes;
    currentUploadSize = completedUploadSize;

    if (totalFilesSize > 0) {
      onProgress(
        Math.min(Math.floor((completedUploadSize / totalFilesSize) * 100), 100)
      );
    } else {
      onProgress(0);
    }

    const resumeObjects: ReservationObject[] = await Promise.all(
      filesToUpload.map(async ([{ id, name, size }]) => {
        let uploaded = 0;
        try {
          uploaded = await getUploadedFileSize({
            reservationUid,
            clientId: id,
          });
        } catch {
          // treat as fresh upload
        }
        return {
          clientId: id,
          name: name,
          size: uploaded,
          intendedSize: size,
        };
      })
    );

    const resumeUpdateRequest: UpdateReservationRequest = {
      uid: reservationUid,
      subject: draft.subject,
      description: draft.description,
      anonSender: draft.anon.name,
      anonEmail: draft.anon.email,
      objects: resumeObjects,
      fields: draft.dynamicFormData ?? undefined,
    };

    throwIfAborted(signal);

    await updateReservation(
      reservationUid,
      reservationToken,
      resumeUpdateRequest
    );
  } else {
    const structuredFields = draft.dynamicFormData ?? undefined;

    filesToUpload = draft.files;
    totalFilesSize = draft.files.reduce((acc, file) => acc + file[0].size, 0);
    onProgress(0);
    completedUploadSize = 0;
    currentUploadSize = 0;

    const createRequest: CreateMailboxReservationRequest = {
      subject: draft.subject,
      description: draft.description,
      anonSender: draft.anon.name,
      anonEmail: draft.anon.email,
    };

    // create reservation
    const reservationCreationResult: ReservationCreationResult =
      await createMailboxReservation(vanityLink, createRequest);

    if (!reservationCreationResult.reservationToken) {
      throw Error(
        "Could not create reservation. Please try refreshing the page."
      );
    }

    reservationUid = reservationCreationResult.reservation.uid;
    reservationToken = reservationCreationResult.reservationToken;

    const reservationObjects: ReservationObject[] = await Promise.all(
      draft.files.map(async ([{ id, name, size }]) => {
        let uploaded = 0;
        try {
          uploaded = await getUploadedFileSize({
            reservationUid,
            clientId: id,
          });
        } catch {
          // treat as fresh upload
        }
        return {
          clientId: id,
          name: name,
          size: uploaded,
          intendedSize: size,
        };
      })
    );

    const updateReservationRequest: UpdateReservationRequest = {
      description: draft.description,
      subject: draft.subject,
      uid: reservationUid,
      anonEmail: draft.anon.email,
      anonSender: draft.anon.name,
      objects: reservationObjects,
      fields: structuredFields,
    };

    throwIfAborted(signal);

    await updateReservation(
      reservationUid,
      reservationToken,
      updateReservationRequest
    );
  }

  const pushProgress = (uploadedBytes: number, startByte: number) => {
    currentUploadSize = completedUploadSize + startByte + uploadedBytes;
    let uploadProgress: number =
      totalFilesSize > 0
        ? Math.floor((currentUploadSize / totalFilesSize) * 100)
        : 100;

    // Since, additional bytes of form data is also reported by axios
    // & this additional bytes are reported with different values in different browsers (400 - 500 bytes).
    // There's no reliable way of computing these additional bytes.
    // Therefore for small files (< 50Kb) [uploadProgress] exceeds 100%.
    //
    // Hence, this is done to clip the uploadProgress to not exceed 100%.
    uploadProgress = Math.min(uploadProgress, 100);

    onProgress(uploadProgress);
  };

  try {
    throwIfAborted(signal);

    // A transfer with no file attachments has nothing to upload, so report completion immediately.
    if (totalFilesSize === 0) {
      onProgress(100);
    }

    for (const [{ name, size, id }, blob] of filesToUpload) {
      await initiateUpload({
        currentChunkSize: constants.initialChunkSize,
        uploadData: {
          clientId: id,
          file: blob,
          filename: name,
          startByte: 0,
          totalBytes: size,
        },
        reservationUid,
        reservationToken,
        transferCompletedBytes: completedUploadSize,
        transferTotalBytes: totalFilesSize,
        shouldCheckUploadedFileSize: !!resume,
        retryCount: 0,
        pushProgress: pushProgress,
        signal: signal,
      });
      throwIfAborted(signal);
      completedUploadSize += size;
    }

    throwIfAborted(signal);

    await confirmReservation(reservationUid, reservationToken);

    return { reservationUid, reservationToken };
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) {
      await cancelReservation(reservationUid);
      throw new UploadAbortedError();
    }
    throw error;
  }
}

/**
 *  Initiates the upload process.
 *
 *  If transfer upload is interupted then it will retry uploading transfer
 *  upto {@link MAX_RETRIES} times. If it still fails, to upload, then it will
 *  abort the upload & throw error.
 */
async function initiateUpload({
  reservationUid,
  reservationToken,
  transferCompletedBytes,
  transferTotalBytes,
  uploadData,
  shouldCheckUploadedFileSize,
  currentChunkSize,
  retryCount,
  pushProgress,
  signal,
}: UploadProp): Promise<void> {
  throwIfAborted(signal);

  if (shouldCheckUploadedFileSize) {
    // Check how many bytes of file are already uploaded on the server.
    try {
      uploadData.startByte = await getUploadedFileSize({
        reservationUid: reservationUid,
        clientId: uploadData.clientId,
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        uploadData.startByte = 0;
      } else {
        const isDeviceOffline: boolean =
          axios.isAxiosError(error) && error.code === AxiosError.ERR_NETWORK;

        if (
          (axios.isAxiosError(error) || isDeviceOffline) &&
          retryCount < constants.maxUploadRetries
        ) {
          return await retryUpload({
            reservationUid,
            reservationToken,
            transferCompletedBytes,
            transferTotalBytes,
            uploadData,
            shouldCheckUploadedFileSize,
            currentChunkSize,
            retryCount,
            pushProgress,
            signal,
          });
        } else if (signal?.aborted || isAbortError(error)) {
          throw new UploadAbortedError();
        } else {
          throw error;
        }
      }
    }
  }

  if (uploadData.startByte >= uploadData.totalBytes) {
    pushProgress(uploadData.totalBytes, 0);
    return;
  }

  uploadData.endByte = Math.min(
    uploadData.startByte + currentChunkSize,
    uploadData.totalBytes
  );

  // Actual upload of file chunk.
  try {
    const chunkUploadStartTime: number = Date.now();
    await uploadFileChunk({
      clientId: uploadData.clientId,
      startByte: uploadData.startByte,
      endByte: uploadData.endByte,
      totalBytes: uploadData.totalBytes,
      file: uploadData.file,
      filename: uploadData.filename,
      reservationUid: reservationUid,
      onUploadProgress: (progressEvent) => {
        pushProgress(progressEvent.loaded, uploadData.startByte);
      },
      signal: signal,
    });

    const uploadedBytes = uploadData.endByte;

    // Reset retry counter.
    retryCount = 0;

    // Time (in seconds) took to upload one chunk
    const chunkUploadDuration: number = Math.floor(
      (Date.now() - chunkUploadStartTime) / 1000
    );

    const newChunkSize = calculateAndGetNewChunkSize(
      chunkUploadDuration,
      currentChunkSize
    );

    if (uploadedBytes < uploadData.totalBytes) {
      // upload is not done yet, do another recursion
      uploadData.startByte = uploadedBytes;

      return await initiateUpload({
        currentChunkSize: newChunkSize,
        shouldCheckUploadedFileSize: false,
        reservationUid: reservationUid,
        reservationToken,
        transferCompletedBytes,
        transferTotalBytes,
        uploadData: uploadData,
        retryCount: retryCount,
        pushProgress: pushProgress,
        signal: signal,
      });
    } else {
      // done with this file!
      return;
    }
  } catch (error) {
    if (isMimeTypeRejection(error)) {
      throw new MimeTypeRejectedError({
        fileName: uploadData.filename,
        clientId: uploadData.clientId,
        reservationUid,
        reservationToken,
        completedBytes: transferCompletedBytes,
        totalBytes: transferTotalBytes,
      });
    }

    const isDeviceOffline: boolean =
      axios.isAxiosError(error) && error.code === AxiosError.ERR_NETWORK;

    if (
      (axios.isAxiosError(error) || isDeviceOffline) &&
      retryCount < constants.maxUploadRetries
    ) {
      // do another retry attempt
      return await retryUpload({
        reservationUid,
        reservationToken,
        transferCompletedBytes,
        transferTotalBytes,
        uploadData,
        shouldCheckUploadedFileSize,
        currentChunkSize,
        retryCount,
        pushProgress,
        signal,
      });
    } else if (signal?.aborted || isAbortError(error)) {
      throw new UploadAbortedError();
    } else {
      // some limitations have most likely been exceeded; no point in retrying the upload
      // or exhausted all retry attempts; giving up
      throw error;
    }
  }
}

const isMimeTypeRejection = (error: unknown): boolean => {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 403 &&
    (error.response.data as ApiException)?.error?.code === 403150
  );
};

async function retryUpload(retryUploadProp: UploadProp): Promise<void> {
  retryUploadProp.retryCount++;

  const waitingTimeMs = Math.min(
    2 ** retryUploadProp.retryCount * 1000,
    constants.maxDelayBetweenRetriesMs
  );

  await delayWithAbort(waitingTimeMs, retryUploadProp.signal);

  retryUploadProp.uploadData.startByte = 0;
  retryUploadProp.shouldCheckUploadedFileSize = true;

  // Retry chunk upload again.
  return await initiateUpload(retryUploadProp);
}

const delayWithAbort = async (
  ms: number,
  signal?: AbortSignal
): Promise<void> => {
  if (!signal) {
    await delay(ms);
    return;
  }

  if (signal.aborted) {
    throw new UploadAbortedError();
  }

  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      signal.removeEventListener("abort", onAbort);
      reject(new UploadAbortedError());
    };

    signal.addEventListener("abort", onAbort, { once: true });

    if (signal.aborted) {
      onAbort();
    }
  });
};

const isAbortError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  if (error instanceof UploadAbortedError) {
    return true;
  }

  if (
    axios.isAxiosError(error) &&
    (error.code === AxiosError.ERR_CANCELED || error.code === "ERR_CANCELED")
  ) {
    return true;
  }

  if (error instanceof Error) {
    return error.name === "CanceledError" || error.name === "AbortError";
  }

  return false;
};

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw new UploadAbortedError();
  }
};

/**
 * Returns new chunk size based on time it took to upload a chunk.
 *
 * @param chunkUploadDuration
 */
const calculateAndGetNewChunkSize = (
  chunkUploadDuration: number, // seconds
  currentChunkSize: number
) => {
  const chunkFactor: number =
    constants.idealChunkUploadDuration / chunkUploadDuration;

  const optimalChunkSize: number = Math.round(currentChunkSize * chunkFactor);

  const clampedChunkSize: number = Math.min(
    optimalChunkSize,
    constants.maxChunkSize
  );

  return clampedChunkSize;
};
